/**
 * Bulk User Upload
 *
 * Create multiple users in SOLUM SaaS via the B2C API.
 * Supports manual entry and CSV upload.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from './axiosInstance';
import ProtectedComponent from './ProtectedComponent';
import './styles/BulkUserUpload.css';

// Validation patterns
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const COMPANY_CODE_REGEX = /^[A-Za-z0-9]{2,10}$/;

// Access level options (matches SOLUM SaaS roles)
const ACCESS_LEVELS = [
  { value: '1', label: 'Admin (1)' },
  { value: '2', label: 'Store Manager (2)' },
  { value: '3', label: 'Dept. Manager (3)' },
  { value: '4', label: 'Employee (4)' },
  { value: '5', label: 'Store Operation (5)' }
];

// Status Badge Component
const StatusBadge = ({ status }) => {
  const colors = {
    success: 'green',
    partial: 'yellow',
    failed: 'red'
  };
  const labels = {
    success: 'Success',
    partial: 'Partial',
    failed: 'Failed'
  };
  return <span className={`buu-status-badge ${colors[status] || 'gray'}`}>{labels[status] || status}</span>;
};

const BulkUserUpload = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState('csv');

  // Manual entry state
  const [companyCode, setCompanyCode] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [accessLevel, setAccessLevel] = useState('1');
  const [userList, setUserList] = useState([]);

  // CSV upload state
  const [csvFile, setCsvFile] = useState(null);
  const [csvUsers, setCsvUsers] = useState([]);
  const [csvCompanyCode, setCsvCompanyCode] = useState('');
  const [csvError, setCsvError] = useState(null);

  // Creation state
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Console output state
  const [consoleLogs, setConsoleLogs] = useState([]);

  // History state
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPagination, setHistoryPagination] = useState({ page: 1, total: 0, totalPages: 0 });
  const [expandedHistory, setExpandedHistory] = useState(null);

  // Validation state
  const [validationErrors, setValidationErrors] = useState({});

  // Console log helper
  const addLog = useCallback((message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setConsoleLogs(prev => [...prev.slice(-49), { timestamp, message, type }]); // Keep last 50
  }, []);

  // Load history when switching to history tab
  const loadHistory = useCallback(async (page = 1) => {
    setHistoryLoading(true);
    try {
      const response = await axiosInstance.get(`/bulk-user-upload/history?page=${page}&limit=20`);
      setHistory(response.data.history);
      setHistoryPagination(response.data.pagination);
    } catch (err) {
      console.error('Failed to load history:', err);
    }
    setHistoryLoading(false);
  }, []);

  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory(1);
    }
  }, [activeTab, loadHistory]);

  // Validate single user entry
  const validateUser = () => {
    const errors = {};

    if (!companyCode.trim()) {
      errors.companyCode = 'Company code is required';
    } else if (!COMPANY_CODE_REGEX.test(companyCode.trim())) {
      errors.companyCode = 'Company code must be 2-10 alphanumeric characters';
    }

    if (!email.trim()) {
      errors.email = 'Email is required';
    } else if (!EMAIL_REGEX.test(email.trim())) {
      errors.email = 'Invalid email format';
    }

    if (!password.trim()) {
      errors.password = 'Password is required';
    } else if (password.trim().length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Add user to list
  const handleAddToList = () => {
    if (!validateUser()) return;

    // Check for duplicates by email
    if (userList.some(u => u.emailAddress === email.trim())) {
      setValidationErrors({ email: 'This email already exists in the list' });
      return;
    }

    // Use email as account (username)
    setUserList([...userList, {
      account: email.trim(),
      password: password.trim(),
      emailAddress: email.trim(),
      accessLevel
    }]);

    // Clear inputs except company code
    setPassword('');
    setEmail('');
    setAccessLevel('1');
    setValidationErrors({});
  };

  // Remove user from list
  const handleRemoveFromList = (index) => {
    setUserList(userList.filter((_, i) => i !== index));
  };

  // Create users (manual method)
  const handleCreateUsers = async () => {
    if (userList.length === 0) {
      setError('Add at least one user to the list');
      return;
    }

    if (!companyCode.trim() || !COMPANY_CODE_REGEX.test(companyCode.trim())) {
      setError('Valid company code is required');
      return;
    }

    setCreating(true);
    setError(null);
    setResult(null);
    addLog(`Creating ${userList.length} user(s) for company ${companyCode.trim().toUpperCase()}...`, 'info');

    try {
      const response = await axiosInstance.post('/bulk-user-upload/create', {
        company: companyCode.trim().toUpperCase(),
        users: userList,
        method: 'manual'
      });

      addLog(`200 OK - ${response.data.successCount}/${response.data.usersRequested} users created`, 'success');

      // Log individual user results
      response.data.usersCreated?.forEach(u => {
        if (u.success) {
          addLog(`  ${u.emailAddress}: SUCCESS`, 'success');
        } else {
          addLog(`  ${u.emailAddress}: ${u.message}`, 'error');
        }
      });

      // Log store mapping results
      if (response.data.storeMappingResults?.length > 0) {
        response.data.storeMappingResults.forEach(r => {
          if (r.success) {
            addLog(`Store mapping: ${r.email} -> ${r.mapped.join(', ')}`, 'success');
          } else {
            addLog(`Store mapping failed: ${r.email} - ${r.error || r.failed?.join(', ')}`, 'error');
          }
        });
      }

      setResult(response.data);
      if (response.data.status === 'success') {
        setUserList([]); // Clear list on full success
        setCompanyCode('');
      }
    } catch (err) {
      const status = err.response?.status || 'ERR';
      const msg = err.response?.data?.responseMessage || err.response?.data?.error || err.message;
      addLog(`${status} ERROR - ${msg}`, 'error');
      setError(err.response?.data?.responseMessage || err.response?.data?.error || 'Failed to create users');
    }

    setCreating(false);
  };

  // Handle CSV file selection
  const handleCsvSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setCsvFile(file);
    setCsvError(null);
    setCsvUsers([]);

    // Extract company code from filename
    // Expected format: User_Request_Company_XXX.csv
    const match = file.name.match(/User_Request_Company_([A-Za-z0-9]+)\.csv/i);
    if (match) {
      setCsvCompanyCode(match[1].toUpperCase());
    } else {
      // Try to find any alphanumeric code before .csv
      const fallback = file.name.match(/([A-Za-z0-9]{2,10})\.csv$/i);
      if (fallback) {
        setCsvCompanyCode(fallback[1].toUpperCase());
      } else {
        setCsvCompanyCode('');
        setCsvError('Could not extract company code from filename. Expected format: User_Request_Company_XXX.csv');
      }
    }

    // Parse CSV
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const lines = text.split(/\r?\n/).filter(line => line.trim());

        // Check for header row
        const firstLine = lines[0].toLowerCase();
        const hasHeader = firstLine.includes('name') || firstLine.includes('password') || firstLine.includes('email');

        const dataLines = hasHeader ? lines.slice(1) : lines;
        const parsedUsers = [];

        for (let i = 0; i < dataLines.length; i++) {
          const line = dataLines[i].trim();
          if (!line) continue;

          // Split by comma (handle quoted values)
          const parts = line.split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/).map(p => p.trim().replace(/^"|"$/g, ''));

          if (parts.length < 4) {
            setCsvError(`Line ${i + (hasHeader ? 2 : 1)} has less than 4 columns (name, password, emailaddress, accesslevel)`);
            return;
          }

          const [csvAccount, csvPassword, csvEmail, csvAccessLevel, csvStoreCodes] = parts;

          if (!csvAccount || !csvPassword || !csvEmail || !csvAccessLevel) {
            setCsvError(`Line ${i + (hasHeader ? 2 : 1)} has empty values`);
            return;
          }

          if (!EMAIL_REGEX.test(csvEmail)) {
            setCsvError(`Line ${i + (hasHeader ? 2 : 1)} has invalid email: ${csvEmail}`);
            return;
          }

          // Validate access level (1-5)
          const accessLevelNum = csvAccessLevel.trim();
          if (!['1', '2', '3', '4', '5'].includes(accessLevelNum)) {
            setCsvError(`Line ${i + (hasHeader ? 2 : 1)} has invalid access level: ${csvAccessLevel} (must be 1-5)`);
            return;
          }

          // Parse store codes (semicolon-separated, optional)
          const storeCodes = csvStoreCodes
            ? csvStoreCodes.trim().split(';').map(s => s.trim()).filter(s => s)
            : [];

          parsedUsers.push({
            account: csvAccount,
            password: csvPassword,
            emailAddress: csvEmail,
            accessLevel: accessLevelNum,
            storeCodes: storeCodes
          });
        }

        if (parsedUsers.length === 0) {
          setCsvError('No valid users found in CSV');
          return;
        }

        setCsvUsers(parsedUsers);
      } catch (err) {
        setCsvError('Failed to parse CSV: ' + err.message);
      }
    };
    reader.readAsText(file);
  };

  // Create users from CSV
  const handleCreateFromCsv = async () => {
    if (csvUsers.length === 0) {
      setCsvError('No users to create');
      return;
    }

    if (!csvCompanyCode || !COMPANY_CODE_REGEX.test(csvCompanyCode)) {
      setCsvError('Valid company code is required');
      return;
    }

    setCreating(true);
    setError(null);
    setResult(null);
    addLog(`Creating ${csvUsers.length} user(s) for company ${csvCompanyCode.toUpperCase()} from CSV...`, 'info');

    try {
      const response = await axiosInstance.post('/bulk-user-upload/create', {
        company: csvCompanyCode.toUpperCase(),
        users: csvUsers,
        method: 'csv',
        csvFilename: csvFile?.name
      });

      addLog(`200 OK - ${response.data.successCount}/${response.data.usersRequested} users created`, 'success');

      // Log individual user results
      response.data.usersCreated?.forEach(u => {
        if (u.success) {
          addLog(`  ${u.emailAddress}: SUCCESS`, 'success');
        } else {
          addLog(`  ${u.emailAddress}: ${u.message}`, 'error');
        }
      });

      // Log store mapping results
      if (response.data.storeMappingResults?.length > 0) {
        response.data.storeMappingResults.forEach(r => {
          if (r.success) {
            addLog(`Store mapping: ${r.email} -> ${r.mapped.join(', ')}`, 'success');
          } else {
            addLog(`Store mapping failed: ${r.email} - ${r.error || r.failed?.join(', ')}`, 'error');
          }
        });
      }

      setResult(response.data);
      if (response.data.status === 'success') {
        setCsvFile(null);
        setCsvUsers([]);
        setCsvCompanyCode('');
      }
    } catch (err) {
      const status = err.response?.status || 'ERR';
      const msg = err.response?.data?.responseMessage || err.response?.data?.error || err.message;
      addLog(`${status} ERROR - ${msg}`, 'error');
      setError(err.response?.data?.responseMessage || err.response?.data?.error || 'Failed to create users');
    }

    setCreating(false);
  };

  // Format date for display
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Download sample CSV
  const handleDownloadSampleCsv = () => {
    const sampleData = `name,password,emailaddress,accesslevel,storecodes
jsmith,SecurePass123!,jsmith@company.com,1,E001;E002
mjones,SecurePass456!,mjones@company.com,2,E003
alee,SecurePass789!,alee@company.com,4,`;

    const blob = new Blob([sampleData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'User_Request_Company_COMPANYCODEHERE.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <ProtectedComponent functionCode="SA10" functionName="Bulk User Upload">
      <div className="buu-container">
        {/* Header */}
        <div className="buu-header">
          <h1>Bulk User Upload</h1>
          <p>Create multiple users in SOLUM SaaS via CSV or manual entry</p>
        </div>

      {/* Tabs */}
      <div className="buu-tabs">
        <button
          className={activeTab === 'csv' ? 'active' : ''}
          onClick={() => setActiveTab('csv')}
        >
          CSV Upload
        </button>
        <button
          className={activeTab === 'manual' ? 'active' : ''}
          onClick={() => setActiveTab('manual')}
        >
          Manual Entry
        </button>
        <button
          className={activeTab === 'history' ? 'active' : ''}
          onClick={() => setActiveTab('history')}
        >
          History
        </button>
      </div>

      {/* Content */}
      <div className="buu-content">
        {/* Error/Result Messages */}
        {error && (
          <div className="buu-error">
            {error}
            <button onClick={() => setError(null)}>Dismiss</button>
          </div>
        )}

        {result && (
          <div className={`buu-result ${result.status}`}>
            <div className="buu-result-header">
              <StatusBadge status={result.status} />
              <span>
                {result.successCount} of {result.usersRequested} users created
              </span>
            </div>
            <div className="buu-result-details">
              {result.usersCreated?.map((u, i) => (
                <div key={i} className={`buu-result-user ${u.success ? 'success' : 'failed'}`}>
                  <span className="account">{u.account}</span>
                  <span className="email">{u.emailAddress}</span>
                  <span className="message">{u.message}</span>
                </div>
              ))}
            </div>
            <button onClick={() => setResult(null)}>Close</button>
          </div>
        )}

        {/* Manual Entry Tab */}
        {activeTab === 'manual' && (
          <div className="buu-manual">
            <div className="buu-form-section">
              <h3>Add User</h3>

              <div className="buu-form-grid">
                <div className="buu-form-group">
                  <label htmlFor="buu-company-code">Company Code</label>
                  <input
                    id="buu-company-code"
                    type="text"
                    value={companyCode}
                    onChange={(e) => setCompanyCode(e.target.value.toUpperCase())}
                    className={validationErrors.companyCode ? 'error' : ''}
                  />
                  {validationErrors.companyCode && (
                    <span className="buu-error-text">{validationErrors.companyCode}</span>
                  )}
                </div>

                <div className="buu-form-group">
                  <label htmlFor="buu-email">Email Address</label>
                  <input
                    id="buu-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={validationErrors.email ? 'error' : ''}
                  />
                  {validationErrors.email && (
                    <span className="buu-error-text">{validationErrors.email}</span>
                  )}
                </div>

                <div className="buu-form-group">
                  <label htmlFor="buu-password">Password</label>
                  <input
                    id="buu-password"
                    type="text"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={validationErrors.password ? 'error' : ''}
                  />
                  {validationErrors.password && (
                    <span className="buu-error-text">{validationErrors.password}</span>
                  )}
                </div>

                <div className="buu-form-group">
                  <label htmlFor="buu-access-level">Access Level</label>
                  <select id="buu-access-level" value={accessLevel} onChange={(e) => setAccessLevel(e.target.value)}>
                    {ACCESS_LEVELS.map(level => (
                      <option key={level.value} value={level.value}>{level.label}</option>
                    ))}
                  </select>
                </div>

                <div className="buu-form-group buu-form-actions">
                  <button className="buu-btn-add" onClick={handleAddToList}>
                    + Add to List
                  </button>
                </div>
              </div>
            </div>

            {/* User List Table */}
            {userList.length > 0 && (
              <div className="buu-list-section">
                <h3>Users to Create ({userList.length})</h3>
                <table className="buu-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Password</th>
                      <th>Access Level</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userList.map((user, index) => (
                      <tr key={index}>
                        <td>{user.emailAddress}</td>
                        <td>{'*'.repeat(Math.min(user.password.length, 8))}</td>
                        <td>Level {user.accessLevel}</td>
                        <td>
                          <button
                            className="buu-btn-remove"
                            onClick={() => handleRemoveFromList(index)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="buu-create-actions">
                  <button
                    className="buu-btn-create"
                    onClick={handleCreateUsers}
                    disabled={creating}
                  >
                    {creating ? 'Creating Users...' : `Create ${userList.length} User(s)`}
                  </button>
                  <button
                    className="buu-btn-clear"
                    onClick={() => setUserList([])}
                    disabled={creating}
                  >
                    Clear List
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* CSV Upload Tab */}
        {activeTab === 'csv' && (
          <div className="buu-csv">
            <div className="buu-form-section">
              <h3>Upload CSV File</h3>
              <p className="buu-help-text">
                Expected filename format: <code>User_Request_Company_COMPANYCODEHERE.csv</code>
                <br />
                CSV format: <code>name, password, emailaddress, accesslevel, storecodes</code>
                <br />
                Access levels: 1=Admin, 2=Store Manager, 3=Dept. Manager, 4=Employee, 5=Store Operation
                <br />
                Store codes: semicolon-separated (e.g., <code>E001;E002;E003</code>) - optional
              </p>

              <button className="buu-download-sample" onClick={handleDownloadSampleCsv}>
                📥 Download Sample CSV
              </button>

              <div className="buu-file-upload">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleCsvSelect}
                  id="csv-file-input"
                />
                <label htmlFor="csv-file-input" className="buu-file-label">
                  {csvFile ? csvFile.name : 'Choose CSV File...'}
                </label>
              </div>

              {csvError && (
                <div className="buu-csv-error">{csvError}</div>
              )}

              {csvCompanyCode && (
                <div className="buu-form-group" style={{ maxWidth: '300px' }}>
                  <label htmlFor="csv-company-code">Extracted Company Code</label>
                  <input
                    id="csv-company-code"
                    type="text"
                    value={csvCompanyCode}
                    onChange={(e) => setCsvCompanyCode(e.target.value.toUpperCase())}
                    placeholder="Company code from filename"
                  />
                </div>
              )}

              {csvUsers.length > 0 && (
                <>
                  <h4>Preview ({csvUsers.length} users)</h4>
                  <table className="buu-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Password</th>
                        <th>Email</th>
                        <th>Access Level</th>
                        <th>Store Codes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvUsers.slice(0, 10).map((user, index) => (
                        <tr key={index}>
                          <td>{user.account}</td>
                          <td>{'*'.repeat(Math.min(user.password.length, 8))}</td>
                          <td>{user.emailAddress}</td>
                          <td>Level {user.accessLevel}</td>
                          <td>{user.storeCodes?.join('; ') || '-'}</td>
                        </tr>
                      ))}
                      {csvUsers.length > 10 && (
                        <tr>
                          <td colSpan={5} className="buu-more">
                            ...and {csvUsers.length - 10} more
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  <div className="buu-create-actions">
                    <button
                      className="buu-btn-create"
                      onClick={handleCreateFromCsv}
                      disabled={creating}
                    >
                      {creating ? 'Creating Users...' : `Create ${csvUsers.length} User(s)`}
                    </button>
                    <button
                      className="buu-btn-clear"
                      onClick={() => {
                        setCsvFile(null);
                        setCsvUsers([]);
                        setCsvCompanyCode('');
                        setCsvError(null);
                      }}
                      disabled={creating}
                    >
                      Clear
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="buu-history">
            {historyLoading ? (
              <div className="buu-loading">Loading history...</div>
            ) : history.length === 0 ? (
              <div className="buu-empty">No upload history yet</div>
            ) : (
              <>
                <table className="buu-table buu-history-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Company</th>
                      <th>Method</th>
                      <th>Users</th>
                      <th>Success</th>
                      <th>Failed</th>
                      <th>Status</th>
                      <th>By</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((entry) => (
                      <React.Fragment key={entry._id}>
                        <tr>
                          <td>{formatDate(entry.timestamp)}</td>
                          <td><code>{entry.company}</code></td>
                          <td>{entry.method === 'csv' ? 'CSV' : 'Manual'}</td>
                          <td>{entry.usersRequested}</td>
                          <td className="success-count">{entry.successCount}</td>
                          <td className="failure-count">{entry.failureCount}</td>
                          <td><StatusBadge status={entry.status} /></td>
                          <td>{entry.performedBy?.displayName || entry.performedBy?.email}</td>
                          <td>
                            <button
                              className="buu-btn-details"
                              onClick={() => setExpandedHistory(expandedHistory === entry._id ? null : entry._id)}
                            >
                              {expandedHistory === entry._id ? 'Hide' : 'Show'}
                            </button>
                          </td>
                        </tr>
                        {expandedHistory === entry._id && (
                          <tr className="buu-history-details-row">
                            <td colSpan={9}>
                              <div className="buu-history-details">
                                {entry.csvFilename && (
                                  <p><strong>CSV File:</strong> {entry.csvFilename}</p>
                                )}
                                {entry.error && (
                                  <p className="buu-history-error"><strong>Error:</strong> {entry.error}</p>
                                )}
                                <table className="buu-table buu-mini-table">
                                  <thead>
                                    <tr>
                                      <th>Name</th>
                                      <th>Email</th>
                                      <th>Result</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {entry.usersCreated?.map((u, i) => (
                                      <tr key={i} className={u.success ? 'success-row' : 'failed-row'}>
                                        <td>{u.account}</td>
                                        <td>{u.emailAddress}</td>
                                        <td>{u.message}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>

                {/* Pagination */}
                {historyPagination.totalPages > 1 && (
                  <div className="buu-pagination">
                    <button
                      disabled={historyPagination.page <= 1}
                      onClick={() => loadHistory(historyPagination.page - 1)}
                    >
                      Previous
                    </button>
                    <span>
                      Page {historyPagination.page} of {historyPagination.totalPages}
                    </span>
                    <button
                      disabled={historyPagination.page >= historyPagination.totalPages}
                      onClick={() => loadHistory(historyPagination.page + 1)}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Console Output */}
        {consoleLogs.length > 0 && (
          <div className="buu-console">
            <div className="buu-console-header">
              <span>Console Output</span>
              <button onClick={() => setConsoleLogs([])}>Clear</button>
            </div>
            <div className="buu-console-body">
              {consoleLogs.map((log, i) => (
                <div key={i} className={`buu-console-line ${log.type}`}>
                  <span className="buu-console-time">[{log.timestamp}]</span>
                  <span className="buu-console-msg">{log.message}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
    </ProtectedComponent>
  );
};

export default BulkUserUpload;
